import { errorHandlerReport } from "@mgdx-libs/error-handler";

import { MynaHomeWebManagerError } from "./MynaHomeWebManagerError";
import { MynaHomeWebResponseParams } from "./parseMynaHomeWebResponseParams";

/**
 * マイナ在宅受付Web提供APIの処理結果を表す型とオブジェクト
 */
export const MynaHomeWebResult = {
  /**
   * 成功: 同意の登録/更新/取消しが完了
   */
  SUCCEEDED: "SUCCEEDED",
  /**
   * 成功: 完了、提出可能な電子処方箋なし
   */
  SUCCEEDED_EPX_UNAVAILABLE: "SUCCEEDED_EPX_UNAVAILABLE",
  /**
   * 成功: 完了、電子処方箋提出済み
   */
  SUCCEEDED_EPX_SUBMITTED: "SUCCEEDED_EPX_SUBMITTED",
  /**
   * 成功: 完了、電子処方箋提出可能だが未提出
   */
  SUCCEEDED_EPX_INCOMPLETE: "SUCCEEDED_EPX_INCOMPLETE",

  /**
   * キャンセル: 同意の登録/更新/取消しがキャンセルされた（照会のみで終了した場合を含む）
   */
  CANCELLED: "CANCELLED",

  /**
   * 未知のエラー: 想定外のレスポンスパターン
   */
  FAILED: "FAILED",
  /**
   * エラー: 未完了、マイナ保険証未登録
   */
  FAILED_UNREGISTERED_MYNA: "FAILED_UNREGISTERED_MYNA",
  /**
   * エラー: 未完了、同一医療機関等に既に未来日付の診療日が登録されている
   */
  FAILED_DUPLICATED: "FAILED_DUPLICATED",
  /**
   * エラー: 未完了、有効な資格情報が確認できなかった
   */
  FAILED_NO_CREDENTIALS: "FAILED_NO_CREDENTIALS",
  /**
   * エラー: 未完了、システムエラー
   */
  FAILED_SYSTEM_ERROR: "FAILED_SYSTEM_ERROR",

  /**
   * 不正なコンテキストを検知したエラー
   */
  ERROR_INVALID_CONTEXT: "ERROR_INVALID_CONTEXT",

  /**
   * 不正なリクエストURLを検知したエラー
   */
  ERROR_INVALID_REQUEST_URL: "ERROR_INVALID_REQUEST_URL",

  /**
   * 不正ないレスポンスパラメーターを検知したエラー
   */
  ERROR_INVALID_RESPONSE_PARAMS: "ERROR_INVALID_RESPONSE_PARAMS",

  /**
   * 認証stateの不正を検知したエラー
   */
  ERROR_INVALID_AUTH_STATE: "ERROR_INVALID_AUTH_STATE",
} as const;

export type MynaHomeWebResult = (typeof MynaHomeWebResult)[keyof typeof MynaHomeWebResult];

/**
 * レスポンスパラメータをMynaHomeWebResultに変換する
 * @param responseParams マイナ在宅受付Web提供APIのレスポンスパラメータ
 * @returns 変換された結果
 */
export const convertMynaHomeWebResult = (responseParams: MynaHomeWebResponseParams): MynaHomeWebResult => {
  try {
    const { result_code, error_code, epx_result_code } = responseParams;

    // キャンセルの処理
    if (result_code === "09") {
      return MynaHomeWebResult.CANCELLED;
    }

    // エラーの処理
    if (result_code === "01") {
      return handleErrorResult(error_code);
    }

    // 成功の処理
    if (result_code === "00") {
      return handleSuccessResult(epx_result_code);
    }

    // 未知のパターンの処理
    errorHandlerReport(`MynaHomeWebManager: Unknown result pattern: ${JSON.stringify(responseParams)}`);
    return MynaHomeWebResult.FAILED;
  } catch (e) {
    throw new MynaHomeWebManagerError(e.message, { cause: e });
  }
};

/**
 * エラー結果を処理する
 * @param error_code エラーコード
 * @returns 対応するMynaHomeWebResult
 */
function handleErrorResult(error_code: string | undefined): MynaHomeWebResult {
  switch (error_code) {
    case "01":
      return MynaHomeWebResult.FAILED_UNREGISTERED_MYNA;
    case "02":
      return MynaHomeWebResult.FAILED_DUPLICATED;
    case "03":
      return MynaHomeWebResult.FAILED_NO_CREDENTIALS;
    case "99":
      return MynaHomeWebResult.FAILED_SYSTEM_ERROR;
    default:
      errorHandlerReport(`MynaHomeWebManager: Unknown response error code: ${error_code}`);
      return MynaHomeWebResult.FAILED;
  }
}

/**
 * 成功結果を処理する
 * @param epx_result_code 電子処方箋結果コード
 * @returns 対応するMynaHomeWebResult
 */
function handleSuccessResult(epx_result_code: string | null | undefined): MynaHomeWebResult {
  switch (epx_result_code) {
    case "00":
      return MynaHomeWebResult.SUCCEEDED_EPX_UNAVAILABLE;
    case "01":
      return MynaHomeWebResult.SUCCEEDED_EPX_SUBMITTED;
    case "02":
      return MynaHomeWebResult.SUCCEEDED_EPX_INCOMPLETE;
    default:
      return MynaHomeWebResult.SUCCEEDED;
  }
}
